'use client';

import { useState, useEffect } from 'react';
import {
  Calendar,
  ChevronRight,
  Users,
  Heart,
  Sparkles,
  Gem,
  Globe,
  Search,
  Ticket,
  UserCheck,
  MapPin,
  BookOpen,
  Utensils,
  MessageCircle,
  Link as LinkIcon,
} from 'lucide-react';
import {
  ResponsiveSheet,
  SheetPage,
  useResponsiveSheet,
} from '@church/nextjs-ui/components/ResponsiveSheet';
import type {
  LifeGroupsInfo,
  GroupCategory,
  FeaturedGroup,
  MeetingAction,
} from '@/types/serve-finder';
import { Img } from '@/lib/asset-url';
import { BaseCard } from './BaseCard';
import { CardCTA } from './CardCTA';

interface GroupsCardProps {
  lifeGroups: LifeGroupsInfo;
}

const CATEGORY_ICONS: Record<GroupCategory['Icon'], React.ComponentType<{ className?: string }>> = {
  users: Users,
  heart: Heart,
  sparkles: Sparkles,
  gem: Gem,
  globe: Globe,
};

const ACTION_ICONS: Record<MeetingAction['Icon'], React.ComponentType<{ className?: string }>> = {
  'map-pin': MapPin,
  'user-check': UserCheck,
  heart: Heart,
  'book-open': BookOpen,
  utensils: Utensils,
  'message-circle': MessageCircle,
  calendar: Calendar,
  link: LinkIcon,
};

// ---------------------------------------------------------------------------
// Slide definitions — styled like CurrentSeriesCard body panels
// ---------------------------------------------------------------------------

interface GroupSlide {
  key: string;
  bg: string;
  bgImage: string | null;
  bgImageOpacity: number;
  render: () => React.ReactNode;
}

function buildSlides(lifeGroups: LifeGroupsInfo): GroupSlide[] {
  const slides: GroupSlide[] = [];

  // Conference slide — colored bg with image on right
  if (lifeGroups.Conference) {
    slides.push({
      key: 'conference',
      bg: '#61bc47',
      bgImage: null,
      bgImageOpacity: 1,
      render: () => (
        <div className="relative flex w-full" style={{ minHeight: 90 }}>
          <div className="relative z-10 flex flex-1 flex-col justify-center px-1 py-2 pr-[30%]">
            <p className="text-[10px] font-semibold tracking-[0.2em] text-white/70 uppercase">
              Groups Conference
            </p>
            <p className="mt-1 text-xl leading-[0.95] font-black text-white uppercase">
              {lifeGroups.Conference!.Date.split('·')[0].trim()}
            </p>
            <p className="mt-1.5 text-xs text-white/70">
              {lifeGroups.Conference!.Description.length > 60
                ? lifeGroups.Conference!.Description.slice(0, 60) + '…'
                : lifeGroups.Conference!.Description}
            </p>
          </div>
          <div className="absolute top-2 right-1 bottom-2 w-[28%] overflow-hidden">
            <Img
              src={lifeGroups.Conference!.Image_URL}
              alt={lifeGroups.Conference!.Title}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      ),
    });
  }

  // Single featured group "Next Meeting" slide — Woodside blue
  if (lifeGroups.Featured_Group) {
    const fg = lifeGroups.Featured_Group;
    const Icon = CATEGORY_ICONS[fg.Icon];
    slides.push({
      key: 'featured-group',
      bg: '#1c2b39',
      bgImage: null,
      bgImageOpacity: 1,
      render: () => (
        <div className="relative flex w-full" style={{ minHeight: 90 }}>
          <div className="relative z-10 flex flex-1 flex-col justify-center px-1 py-2">
            <p className="text-[10px] font-semibold tracking-[0.2em] text-white/50 uppercase">
              Next Meeting
            </p>
            <p className="mt-1 text-2xl leading-[0.9] font-black text-white uppercase">
              {fg.Next_Meeting} · {fg.Time}
            </p>
            <p className="mt-1.5 text-xs text-white/60">{fg.Location}</p>
          </div>
          <div className="absolute top-3 right-2 bottom-3 flex w-[20%] items-center justify-end">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10">
              <Icon className="h-5 w-5 text-white/60" />
            </div>
          </div>
        </div>
      ),
    });
  }

  // "Interested in leading?" slide — Woodside green
  slides.push({
    key: 'lead',
    bg: '#61bc47',
    bgImage: null,
    bgImageOpacity: 1,
    render: () => (
      <div className="relative flex w-full" style={{ minHeight: 90 }}>
        <div className="relative z-10 flex flex-1 flex-col justify-center px-1 py-2">
          <p className="text-[10px] font-semibold tracking-[0.2em] text-white/70 uppercase">
            Life Groups
          </p>
          <p className="mt-1 text-xl leading-[0.95] font-black text-white uppercase">
            Interested in Leading?
          </p>
          <p className="mt-1.5 text-xs text-white/70">
            Start a group and make an impact in your community
          </p>
        </div>
        <div className="absolute top-3 right-2 bottom-3 flex w-[20%] items-center justify-end">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15">
            <Heart className="h-5 w-5 text-white/70" />
          </div>
        </div>
      </div>
    ),
  });

  return slides;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GroupsCard({ lifeGroups }: GroupsCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  const slides = buildSlides(lifeGroups);

  const [slideIndex, setSlideIndex] = useState(0);
  useEffect(() => {
    if (slides.length <= 1) return;
    const interval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % slides.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [slideIndex, slides.length]);

  const activeIndex = slides.length > 0 ? slideIndex % slides.length : 0;
  const activeSlide = slides[activeIndex];

  return (
    <>
      <BaseCard
        variant="standard"
        className="bg-card cursor-pointer text-left"
        onClick={() => setIsOpen(true)}
        header={
          <div className="relative aspect-video w-full overflow-hidden">
            <Img src={lifeGroups.Hero_Image_URL} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-black/20" />

            {/* Dot indicators top-right */}
            {slides.length > 1 && (
              <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
                {slides.map((_, i) => (
                  <div
                    key={i}
                    className="h-1.5 rounded-full transition-all duration-300"
                    style={{
                      width: i === activeIndex ? '16px' : '6px',
                      backgroundColor:
                        i === activeIndex ? 'var(--brand-secondary)' : 'rgba(255,255,255,0.4)',
                    }}
                  />
                ))}
              </div>
            )}

            {/* Date strip at bottom if conference */}
            {lifeGroups.Conference && (
              <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-center gap-1.5 bg-gradient-to-t from-black/60 to-transparent px-4 pt-4 pb-2 text-[11px] font-semibold tracking-widest text-white/80 uppercase">
                <Calendar className="h-3 w-3" />
                Groups Conference · {lifeGroups.Conference.Date}
              </div>
            )}
          </div>
        }
        body={
          slides.length > 0 && activeSlide ? (
            <div className="flex h-full items-stretch px-4 py-2">
              <div
                className="relative w-full overflow-hidden px-3 py-2 transition-colors duration-500"
                style={{ backgroundColor: activeSlide.bg }}
              >
                {activeSlide.bgImage && (
                  <Img
                    src={activeSlide.bgImage}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
                    style={{ opacity: activeSlide.bgImageOpacity }}
                  />
                )}

                <div className="relative z-10 grid h-full" style={{ gridTemplate: '1fr / 1fr' }}>
                  {slides.map((s, i) => {
                    const isActive = i === activeIndex;
                    return (
                      <div
                        key={s.key}
                        className="flex flex-col justify-stretch"
                        style={{
                          gridArea: '1 / 1',
                          opacity: isActive ? 1 : 0,
                          transition: 'opacity 0.4s ease-in-out',
                          pointerEvents: isActive ? 'auto' : 'none',
                        }}
                        aria-hidden={!isActive}
                      >
                        {s.render()}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 pb-3">
              <h3 className="text-foreground text-base leading-tight font-extrabold">
                Life Groups
              </h3>
              <p className="text-muted-foreground mt-2 line-clamp-2 text-sm leading-relaxed">
                {lifeGroups.Tagline}
              </p>
            </div>
          )
        }
        footer={<CardCTA label="Explore Groups" useDefaultColors />}
      />

      {/* Detail sheet */}
      <ResponsiveSheet
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="max-w-3xl"
        noPanelPadding
        panelClassName="bg-card"
        header={
          <div
            className="relative overflow-hidden border-b border-white/10"
            style={{ backgroundColor: '#1c2b39' }}
          >
            <div className="relative z-10 flex items-end gap-4 p-5">
              <div className="h-24 w-24 shrink-0 overflow-hidden sm:h-28 sm:w-28">
                <Img
                  src={lifeGroups.Hero_Image_URL}
                  alt="Life Groups"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1.5 h-1 w-12 bg-[var(--brand-secondary)]" />
                <h2 className="text-xl leading-tight font-light tracking-wide text-white sm:text-2xl">
                  life groups
                </h2>
                <p className="mt-1 text-xs leading-relaxed text-white/50">{lifeGroups.Tagline}</p>
              </div>
            </div>
          </div>
        }
      >
        <SheetPage name="main">
          <MainPage lifeGroups={lifeGroups} />
        </SheetPage>

        <SheetPage name="meeting">
          <MeetingPage lifeGroups={lifeGroups} />
        </SheetPage>

        <SheetPage name="browse">
          <BrowsePage lifeGroups={lifeGroups} />
        </SheetPage>
      </ResponsiveSheet>
    </>
  );
}

// ---------------------------------------------------------------------------
// Sheet pages (child components so they can use useResponsiveSheet)
// ---------------------------------------------------------------------------

function MainPage({ lifeGroups }: { lifeGroups: LifeGroupsInfo }) {
  const { navigate } = useResponsiveSheet();

  return (
    <div>
      <div className="space-y-5 p-5">
        <p className="text-muted-foreground text-sm leading-relaxed">{lifeGroups.Description}</p>

        <div className="space-y-2">
          {/* Groups Conference — thumbnail left of text */}
          {lifeGroups.Conference && (
            <a
              href={lifeGroups.Conference.Registration_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group border-border bg-background flex w-full items-center justify-between gap-3 border p-4 text-left transition-shadow hover:shadow-md"
            >
              <div className="flex items-center gap-3">
                <div className="h-12 w-20 shrink-0 overflow-hidden">
                  <Img
                    src={lifeGroups.Conference.Image_URL}
                    alt={lifeGroups.Conference.Title}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-foreground text-sm font-bold">{lifeGroups.Conference.Title}</p>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {lifeGroups.Conference.Date}
                  </p>
                </div>
              </div>
              <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
            </a>
          )}

          {/* Next Group Meeting */}
          {lifeGroups.Featured_Group && (
            <button
              type="button"
              onClick={() => navigate('meeting')}
              className="group border-border bg-background flex w-full items-center justify-between border p-4 text-left transition-shadow hover:shadow-md"
            >
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 shrink-0 text-[var(--brand-secondary)]" />
                <div>
                  <p className="text-foreground text-sm font-bold">Next Group Meeting</p>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {lifeGroups.Featured_Group.Next_Meeting} · {lifeGroups.Featured_Group.Time}
                  </p>
                </div>
              </div>
              <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
            </button>
          )}

          {/* My Groups */}
          <a
            href={lifeGroups.Find_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group border-border bg-background flex w-full items-center justify-between border p-4 text-left transition-shadow hover:shadow-md"
          >
            <div className="flex items-center gap-3">
              <UserCheck className="h-5 w-5 shrink-0 text-[var(--brand-secondary)]" />
              <div>
                <p className="text-foreground text-sm font-bold">My Groups</p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                  View groups you&apos;re part of
                </p>
              </div>
            </div>
            <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
          </a>

          {/* Lead a Group */}
          <a
            href={lifeGroups.Find_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group border-border bg-background flex w-full items-center justify-between border p-4 text-left transition-shadow hover:shadow-md"
          >
            <div className="flex items-center gap-3">
              <Heart className="h-5 w-5 shrink-0 text-[var(--brand-secondary)]" />
              <div>
                <p className="text-foreground text-sm font-bold">Lead a Group</p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                  Start or lead a Life Group in your area
                </p>
              </div>
            </div>
            <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
          </a>

          {/* Browse by Category — navigates to browse page */}
          {lifeGroups.Categories.length > 0 && (
            <button
              type="button"
              onClick={() => navigate('browse')}
              className="group border-border bg-background flex w-full items-center justify-between border p-4 text-left transition-shadow hover:shadow-md"
            >
              <div className="flex items-center gap-3">
                <Search className="h-5 w-5 shrink-0 text-[var(--brand-secondary)]" />
                <div>
                  <p className="text-foreground text-sm font-bold">Browse by Category</p>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    Men&apos;s, Women&apos;s, Young Adults, and more
                  </p>
                </div>
              </div>
              <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
            </button>
          )}
        </div>
      </div>

      {/* Learn More CTA */}
      <a
        href={lifeGroups.Find_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="border-border flex items-center justify-center gap-2 border-t py-4 text-sm font-semibold tracking-wide text-[var(--brand-secondary)] uppercase transition-opacity hover:opacity-80"
      >
        Learn More
        <ChevronRight className="h-4 w-4" />
      </a>
    </div>
  );
}

function MeetingPage({ lifeGroups }: { lifeGroups: LifeGroupsInfo }) {
  const { goBack } = useResponsiveSheet();
  const fg = lifeGroups.Featured_Group;

  if (!fg) return null;

  const GroupIcon = CATEGORY_ICONS[fg.Icon];

  return (
    <div>
      <div className="space-y-4 p-5">
        <button
          type="button"
          onClick={goBack}
          className="flex items-center gap-1 text-xs font-semibold tracking-wide text-[var(--brand-secondary)] uppercase transition-opacity hover:opacity-80"
        >
          <ChevronRight className="h-4 w-4 rotate-180" />
          Back
        </button>

        {/* Meeting header */}
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--brand-secondary)]/10">
            <GroupIcon className="h-5 w-5 text-[var(--brand-secondary)]" />
          </div>
          <div>
            <h3 className="text-foreground text-base font-bold">{fg.Group_Name}</h3>
            <p className="text-muted-foreground text-xs">{fg.Leaders}</p>
            <p className="text-muted-foreground mt-1 flex items-center gap-1 text-xs">
              <Calendar className="h-3 w-3" />
              {fg.Next_Meeting} · {fg.Time}
            </p>
            <p className="text-muted-foreground mt-0.5 flex items-center gap-1 text-xs">
              <MapPin className="h-3 w-3" />
              {fg.Location}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          {fg.Meeting_Actions.map((action) => {
            const Icon = ACTION_ICONS[action.Icon];
            const content = (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-[var(--brand-secondary)]/10">
                    <Icon className="h-4 w-4 text-[var(--brand-secondary)]" />
                  </div>
                  <div>
                    <p className="text-foreground text-sm font-semibold">{action.Label}</p>
                    <p className="text-muted-foreground text-xs">{action.Description}</p>
                  </div>
                </div>
                <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
              </>
            );

            if (action.URL) {
              return (
                <a
                  key={action.Label}
                  href={action.URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-border bg-background flex w-full items-center justify-between gap-3 border p-3 transition-shadow hover:shadow-md"
                >
                  {content}
                </a>
              );
            }

            return (
              <button
                key={action.Label}
                type="button"
                className="border-border bg-background flex w-full items-center justify-between gap-3 border p-3 text-left opacity-60"
                disabled
              >
                {content}
              </button>
            );
          })}
        </div>
      </div>

      {/* View Group footer CTA */}
      <a
        href={lifeGroups.Find_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="border-border flex items-center justify-center gap-2 border-t py-4 text-sm font-semibold tracking-wide text-[var(--brand-secondary)] uppercase transition-opacity hover:opacity-80"
      >
        <Users className="h-4 w-4" />
        View Group
        <ChevronRight className="h-4 w-4" />
      </a>
    </div>
  );
}

function BrowsePage({ lifeGroups }: { lifeGroups: LifeGroupsInfo }) {
  const { goBack } = useResponsiveSheet();

  return (
    <div>
      <div className="space-y-4 p-5">
        <button
          type="button"
          onClick={goBack}
          className="flex items-center gap-1 text-xs font-semibold tracking-wide text-[var(--brand-secondary)] uppercase transition-opacity hover:opacity-80"
        >
          <ChevronRight className="h-4 w-4 rotate-180" />
          Back
        </button>

        <h3 className="text-foreground text-base font-bold">Browse by Category</h3>

        <div className="space-y-2">
          {lifeGroups.Categories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.Icon];
            return (
              <a
                key={cat.ID}
                href={cat.Browse_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group border-border bg-background flex items-center justify-between gap-3 border p-3 transition-shadow hover:shadow-md"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-[var(--brand-secondary)]/10">
                    <Icon className="h-4 w-4 text-[var(--brand-secondary)]" />
                  </div>
                  <div>
                    <p className="text-foreground text-sm font-semibold">{cat.Label}</p>
                    <p className="text-muted-foreground text-xs">
                      {cat.Schedule} · Next: {cat.Next_Meeting}
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-[var(--brand-secondary)]" />
              </a>
            );
          })}
        </div>
      </div>

      {/* Find a Group CTA */}
      <a
        href={lifeGroups.Find_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="border-border flex items-center justify-center gap-2 border-t py-4 text-sm font-semibold tracking-wide text-[var(--brand-secondary)] uppercase transition-opacity hover:opacity-80"
      >
        <Search className="h-4 w-4" />
        Find a Group Near You
        <ChevronRight className="h-4 w-4" />
      </a>
    </div>
  );
}
